import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getImageGeneratorGeneration,
  getImageGeneratorHistory,
  ImageGeneratorHistoryRecord,
  isImageGeneratorNotFound,
} from "../../app/api/imageGenerator";
import { RootState } from "../../app/store";
import {
  CLIENT_SSO_COLLECTION_BUILD_PATH,
  CLIENT_SSO_IMAGE_GENERATOR_PATH,
  isClientSsoUser,
} from "../../constants/clientSso";
import {
  IMAGE_GENERATOR_SESSION_PARAM,
  imageGeneratorSessionTitle,
} from "./imageGeneratorSession";

const PENDING_STORAGE_PREFIX = "ich-ig-pending-ids";
const PENDING_SHEET_STORAGE_PREFIX = "ich-ig-pending-sheets";
const NOTIFICATION_STORAGE_PREFIX = "ich-ig-notifications";
/** Active jobs: poll often so the open results panel refreshes promptly. */
const POLL_MS = 2500;
const HISTORY_LIMIT = 80;

export type ImageGeneratorJobNotification = {
  id: number;
  title: string;
  status: "generated" | "failed";
  read: boolean;
  at: string;
  errorMessage?: string;
  /** Look job vs design-sheet job — same generation id must not clobber each other. */
  kind?: "look" | "designSheet";
};

type ImageGeneratorJobsValue = {
  sessions: ImageGeneratorHistoryRecord[];
  pendingIds: number[];
  notifications: ImageGeneratorJobNotification[];
  unreadCount: number;
  trackJob: (record: ImageGeneratorHistoryRecord) => void;
  trackSheetJob: (record: ImageGeneratorHistoryRecord) => void;
  updateSession: (record: ImageGeneratorHistoryRecord) => void;
  loadSession: (id: number) => Promise<ImageGeneratorHistoryRecord | null>;
  refreshSessions: () => Promise<void>;
  markRead: (id: number) => void;
  markAllRead: () => void;
  openSession: (id: number) => void;
};

const ImageGeneratorJobsContext = createContext<ImageGeneratorJobsValue | null>(
  null
);

const pendingStorageKey = (userId: string) =>
  `${PENDING_STORAGE_PREFIX}:${userId}`;
const notificationStorageKey = (userId: string) =>
  `${NOTIFICATION_STORAGE_PREFIX}:${userId}`;

const pendingSheetStorageKey = (userId: string) =>
  `${PENDING_SHEET_STORAGE_PREFIX}:${userId}`;

const readPendingSheetIds = (userId: string): number[] =>
  readPendingIdsWithKey(pendingSheetStorageKey(userId));

const readPendingIdsWithKey = (key: string): number[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as number[]) : [];
    return Array.from(
      new Set(parsed.filter(id => Number.isInteger(id) && id > 0))
    );
  } catch {
    return [];
  }
};

const readPendingIds = (userId: string): number[] =>
  readPendingIdsWithKey(pendingStorageKey(userId));

const readNotifications = (userId: string): ImageGeneratorJobNotification[] => {
  try {
    const raw = localStorage.getItem(notificationStorageKey(userId));
    const parsed = raw
      ? (JSON.parse(raw) as ImageGeneratorJobNotification[])
      : [];
    return Array.isArray(parsed) ? parsed.slice(0, 40) : [];
  } catch {
    return [];
  }
};

const sortSessions = (items: ImageGeneratorHistoryRecord[]) =>
  [...items].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  );

export const ImageGeneratorJobsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Only pause IG polling on Collection Build — HOME aliases Image Generator,
  // so using HOME here previously disabled polls on the IG page itself.
  const onCollectionBuildPage = location.pathname.startsWith(
    CLIENT_SSO_COLLECTION_BUILD_PATH
  );
  const userId = useSelector(
    (state: RootState) => state.user.userInfo?.userId || ""
  );
  const group = useSelector(
    (state: RootState) => state.user.userInfo?.group || ""
  );
  const enabled = Boolean(userId && isClientSsoUser(group));
  // CB must never trigger IG history polls; resume when leaving CB.
  const igNetworkEnabled = enabled && !onCollectionBuildPage;

  const [sessions, setSessions] = useState<ImageGeneratorHistoryRecord[]>([]);
  const [pendingIds, setPendingIds] = useState<number[]>([]);
  const [pendingSheetIds, setPendingSheetIds] = useState<number[]>([]);
  const [notifications, setNotifications] = useState<
    ImageGeneratorJobNotification[]
  >([]);
  /** Bumped when a job is tracked so the poll effect runs an immediate tick. */
  const [pollKick, setPollKick] = useState(0);
  const pendingRef = useRef<number[]>([]);
  const pendingSheetRef = useRef<number[]>([]);
  const completingRef = useRef(new Set<number>());
  const completingSheetRef = useRef(new Set<number>());
  const sessionsRef = useRef<ImageGeneratorHistoryRecord[]>([]);
  const inflightGenerationRef = useRef(
    new Map<number, Promise<ImageGeneratorHistoryRecord | null>>()
  );
  const pollInflightRef = useRef(false);
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  sessionsRef.current = sessions;

  const persistPending = useCallback((ids: number[]) => {
    const unique = Array.from(new Set(ids));
    pendingRef.current = unique;
    setPendingIds(unique);
    const currentUserId = userIdRef.current;
    if (currentUserId) {
      localStorage.setItem(
        pendingStorageKey(currentUserId),
        JSON.stringify(unique)
      );
    }
  }, []);

  const persistPendingSheets = useCallback((ids: number[]) => {
    const unique = Array.from(new Set(ids));
    pendingSheetRef.current = unique;
    setPendingSheetIds(unique);
    const currentUserId = userIdRef.current;
    if (currentUserId) {
      localStorage.setItem(
        pendingSheetStorageKey(currentUserId),
        JSON.stringify(unique)
      );
    }
  }, []);

  const completeJob = useCallback(
    (record: ImageGeneratorHistoryRecord) => {
      if (record.status !== "generated" && record.status !== "failed") {
        return;
      }
      if (!pendingRef.current.includes(record.id)) {
        return;
      }
      if (completingRef.current.has(record.id)) {
        return;
      }
      completingRef.current.add(record.id);
      persistPending(pendingRef.current.filter(id => id !== record.id));
      const title = imageGeneratorSessionTitle(record);
      const notification: ImageGeneratorJobNotification = {
        id: record.id,
        title,
        status: record.status,
        read: false,
        at: new Date().toISOString(),
        errorMessage: record.details?.errorMessage,
        kind: "look",
      };
      setNotifications(prev => {
        const next = [
          notification,
          ...prev.filter(
            item => !(item.id === record.id && (item.kind || "look") === "look")
          ),
        ].slice(0, 40);
        const currentUserId = userIdRef.current;
        if (currentUserId) {
          localStorage.setItem(
            notificationStorageKey(currentUserId),
            JSON.stringify(next)
          );
        }
        return next;
      });
      if (record.status === "generated") {
        toast.success(`${title} is ready. Open it from Recents or the bell.`, {
          hideProgressBar: true,
        });
      } else {
        toast.error(
          record.details?.errorMessage || `${title} failed to generate.`,
          { hideProgressBar: true }
        );
      }
    },
    [persistPending]
  );

  const completeSheetJob = useCallback(
    (record: ImageGeneratorHistoryRecord) => {
      const sheet = record.details?.designSheet;
      if (sheet?.status !== "generated" && sheet?.status !== "failed") {
        return;
      }
      if (!pendingSheetRef.current.includes(record.id)) {
        return;
      }
      if (completingSheetRef.current.has(record.id)) {
        return;
      }
      completingSheetRef.current.add(record.id);
      persistPendingSheets(
        pendingSheetRef.current.filter(id => id !== record.id)
      );
      const title = `Design sheet · ${imageGeneratorSessionTitle(record)}`;
      const notification: ImageGeneratorJobNotification = {
        id: record.id,
        title,
        status: sheet.status,
        read: false,
        at: new Date().toISOString(),
        errorMessage: sheet.errorMessage,
        kind: "designSheet",
      };
      setNotifications(prev => {
        const next = [
          notification,
          // Do not replace the look’s success/fail notification when the sheet fails.
          ...prev.filter(
            item => !(item.id === record.id && item.kind === "designSheet")
          ),
        ].slice(0, 40);
        const currentUserId = userIdRef.current;
        if (currentUserId) {
          localStorage.setItem(
            notificationStorageKey(currentUserId),
            JSON.stringify(next)
          );
        }
        return next;
      });
      if (sheet.status === "generated") {
        toast.success(`${title} is ready. Open it from Recents or the bell.`, {
          hideProgressBar: true,
        });
      } else {
        toast.error(sheet.errorMessage || `${title} failed to generate.`, {
          hideProgressBar: true,
        });
      }
    },
    [persistPendingSheets]
  );

  const upsertSessions = useCallback(
    (incoming: ImageGeneratorHistoryRecord[], replace = false) => {
      setSessions(prev => {
        const byId = new Map<number, ImageGeneratorHistoryRecord>();
        if (!replace) {
          prev.forEach(item => {
            byId.set(item.id, item);
          });
        } else {
          // History refresh: keep local-only rows (deep-link / out of first page)
          // so we do not wipe → re-fetch → wipe in a loop.
          const incomingIds = new Set(incoming.map(item => item.id));
          prev.forEach(item => {
            if (!incomingIds.has(item.id)) {
              byId.set(item.id, item);
            }
          });
        }
        incoming.forEach(item => {
          byId.set(item.id, item);
        });
        return sortSessions(Array.from(byId.values()));
      });
    },
    []
  );

  const syncFromHistory = useCallback(
    (items: ImageGeneratorHistoryRecord[]) => {
      upsertSessions(items, true);
      const generatingIds = items
        .filter(item => item.status === "generating")
        .map(item => item.id);
      const nextPending = Array.from(
        new Set([...pendingRef.current, ...generatingIds])
      );
      persistPending(nextPending);
      nextPending.forEach(id => {
        const record = items.find(item => item.id === id);
        if (
          record &&
          (record.status === "generated" || record.status === "failed")
        ) {
          completeJob(record);
        }
      });
      const sheetGeneratingIds = items
        .filter(item => item.details?.designSheet?.status === "generating")
        .map(item => item.id);
      const nextSheetPending = Array.from(
        new Set([...pendingSheetRef.current, ...sheetGeneratingIds])
      );
      persistPendingSheets(nextSheetPending);
      nextSheetPending.forEach(id => {
        const record = items.find(item => item.id === id);
        if (record) {
          completeSheetJob(record);
        }
      });
    },
    [
      completeJob,
      completeSheetJob,
      persistPending,
      persistPendingSheets,
      upsertSessions,
    ]
  );

  const refreshSessions = useCallback(async () => {
    if (!igNetworkEnabled) {
      return;
    }
    try {
      const history = await getImageGeneratorHistory(HISTORY_LIMIT, 0);
      syncFromHistory(history?.items || []);
    } catch {
      // History is optional until the first successful generate.
    }
  }, [igNetworkEnabled, syncFromHistory]);

  const trackJob = useCallback(
    (record: ImageGeneratorHistoryRecord) => {
      upsertSessions([record]);
      if (record.status === "generating") {
        persistPending([...pendingRef.current, record.id]);
        setPollKick(value => value + 1);
        return;
      }
      completeJob(record);
    },
    [completeJob, persistPending, upsertSessions]
  );

  const trackSheetJob = useCallback(
    (record: ImageGeneratorHistoryRecord) => {
      upsertSessions([record]);
      if (record.details?.designSheet?.status === "generating") {
        completingSheetRef.current.delete(record.id);
        persistPendingSheets([...pendingSheetRef.current, record.id]);
        setPollKick(value => value + 1);
        return;
      }
      completeSheetJob(record);
    },
    [completeSheetJob, persistPendingSheets, upsertSessions]
  );

  const updateSession = useCallback(
    (record: ImageGeneratorHistoryRecord) => {
      upsertSessions([record]);
    },
    [upsertSessions]
  );

  const dropMissingIds = useCallback(
    (ids: number[]) => {
      if (!ids.length) {
        return;
      }
      const missing = new Set(ids);
      persistPending(pendingRef.current.filter(id => !missing.has(id)));
      persistPendingSheets(
        pendingSheetRef.current.filter(id => !missing.has(id))
      );
    },
    [persistPending, persistPendingSheets]
  );

  const fetchGeneration = useCallback(async (id: number) => {
    const inflight = inflightGenerationRef.current.get(id);
    if (inflight) {
      return inflight;
    }
    const request = (async (): Promise<ImageGeneratorHistoryRecord | null> => {
      try {
        return (await getImageGeneratorGeneration(id)) || null;
      } catch (error) {
        if (isImageGeneratorNotFound(error)) {
          return null;
        }
        throw error;
      } finally {
        inflightGenerationRef.current.delete(id);
      }
    })();
    inflightGenerationRef.current.set(id, request);
    return request;
  }, []);

  const loadSession = useCallback(
    async (id: number) => {
      const cached = sessionsRef.current.find(item => item.id === id);
      if (
        cached &&
        cached.status !== "generating" &&
        cached.details?.designSheet?.status !== "generating"
      ) {
        completeJob(cached);
        completeSheetJob(cached);
        return cached;
      }
      try {
        const record = await fetchGeneration(id);
        if (record) {
          upsertSessions([record]);
          completeJob(record);
          completeSheetJob(record);
          return record;
        }
        dropMissingIds([id]);
        return null;
      } catch {
        return null;
      }
    },
    [
      completeJob,
      completeSheetJob,
      dropMissingIds,
      fetchGeneration,
      upsertSessions,
    ]
  );

  const markRead = useCallback((id: number) => {
    setNotifications(prev => {
      const next = prev.map(item =>
        item.id === id ? { ...item, read: true } : item
      );
      const currentUserId = userIdRef.current;
      if (currentUserId) {
        localStorage.setItem(
          notificationStorageKey(currentUserId),
          JSON.stringify(next)
        );
      }
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => {
      const next = prev.map(item => ({ ...item, read: true }));
      const currentUserId = userIdRef.current;
      if (currentUserId) {
        localStorage.setItem(
          notificationStorageKey(currentUserId),
          JSON.stringify(next)
        );
      }
      return next;
    });
  }, []);

  const openSession = useCallback(
    (id: number) => {
      markRead(id);
      navigate(
        `${CLIENT_SSO_IMAGE_GENERATOR_PATH}?${IMAGE_GENERATOR_SESSION_PARAM}=${id}`
      );
    },
    [markRead, navigate]
  );

  useEffect(() => {
    if (!enabled || !userId) {
      setSessions([]);
      pendingRef.current = [];
      pendingSheetRef.current = [];
      setPendingIds([]);
      setPendingSheetIds([]);
      setNotifications([]);
      completingRef.current.clear();
      completingSheetRef.current.clear();
      return;
    }
    pendingRef.current = readPendingIds(userId);
    setPendingIds(pendingRef.current);
    pendingSheetRef.current = readPendingSheetIds(userId);
    setPendingSheetIds(pendingSheetRef.current);
    setNotifications(readNotifications(userId));
    completingRef.current.clear();
    completingSheetRef.current.clear();
  }, [enabled, userId]);

  useEffect(() => {
    if (!igNetworkEnabled) {
      return;
    }
    void refreshSessions();
  }, [igNetworkEnabled, refreshSessions, userId]);

  useEffect(() => {
    if (!igNetworkEnabled || (!pendingIds.length && !pendingSheetIds.length)) {
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      if (
        document.visibilityState === "hidden" ||
        cancelled ||
        pollInflightRef.current
      ) {
        return;
      }
      const ids = Array.from(
        new Set([...pendingRef.current, ...pendingSheetRef.current])
      );
      if (!ids.length) {
        return;
      }
      pollInflightRef.current = true;
      try {
        const missingIds: number[] = [];
        const fresh: ImageGeneratorHistoryRecord[] = [];
        const settled: ImageGeneratorHistoryRecord[] = [];
        await Promise.all(
          ids.map(async id => {
            const known = sessionsRef.current.find(item => item.id === id);
            const lookBusy = known?.status === "generating";
            const sheetBusy =
              known?.details?.designSheet?.status === "generating";
            // Always re-fetch while look/sheet is busy. Settled rows only need
            // completeJob cleanup if they are still on the pending list.
            if (known && !lookBusy && !sheetBusy) {
              settled.push(known);
              return;
            }
            try {
              const record = await fetchGeneration(id);
              if (!record) {
                missingIds.push(id);
                return;
              }
              fresh.push(record);
            } catch {
              // Leave id pending for the next poll.
            }
          })
        );
        if (cancelled) {
          return;
        }
        if (missingIds.length) {
          dropMissingIds(missingIds);
        }
        if (fresh.length) {
          upsertSessions(fresh);
        }
        [...fresh, ...settled].forEach(record => {
          completeJob(record);
          completeSheetJob(record);
        });
      } catch {
        // Keep the last known sessions if a poll fails.
      } finally {
        pollInflightRef.current = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void tick();
      }
    };

    const timer = window.setInterval(() => {
      void tick();
    }, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    completeJob,
    completeSheetJob,
    dropMissingIds,
    igNetworkEnabled,
    fetchGeneration,
    pendingIds.length,
    pendingSheetIds.length,
    pollKick,
    upsertSessions,
  ]);

  const unreadCount = useMemo(
    () => notifications.filter(item => !item.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({
      sessions,
      pendingIds,
      notifications,
      unreadCount,
      trackJob,
      trackSheetJob,
      updateSession,
      loadSession,
      refreshSessions,
      markRead,
      markAllRead,
      openSession,
    }),
    [
      loadSession,
      markAllRead,
      markRead,
      notifications,
      openSession,
      pendingIds,
      refreshSessions,
      sessions,
      trackJob,
      trackSheetJob,
      updateSession,
      unreadCount,
    ]
  );

  return (
    <ImageGeneratorJobsContext.Provider value={value}>
      {children}
    </ImageGeneratorJobsContext.Provider>
  );
};

export const useImageGeneratorJobs = () => {
  const value = useContext(ImageGeneratorJobsContext);
  if (!value) {
    throw new Error("ImageGeneratorJobsProvider is required.");
  }
  return value;
};

export const useImageGeneratorJobsOptional = () =>
  useContext(ImageGeneratorJobsContext);
